import { useState, useEffect } from 'react';
import { getAdminPlans, createAdminPlan, updateAdminPlan, deleteAdminPlan } from '../../api/adminApi';

const cyan = '#00e5ff';
const EMPTY = { planName: '', amount: '', planType: 'basic' };

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
};

const AdminPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'add' | plan object
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const isMobile = useIsMobile();

  const load = () => {
    setLoading(true);
    getAdminPlans().then((res) => setPlans(res.data.plans)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm(EMPTY); setMsg(''); setModal('add'); };
  const openEdit = (p) => { setForm({ planName: p.planName, amount: p.amount, planType: p.planType }); setMsg(''); setModal(p); };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg('');
    try {
      if (modal === 'add') {
        await createAdminPlan(form);
      } else {
        await updateAdminPlan(modal._id, form);
      }
      load();
      setModal(null);
    } catch (err) {
      setMsg(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this plan?')) return;
    setDeletingId(id);
    try {
      await deleteAdminPlan(id);
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Delete failed');
    } finally { setDeletingId(null); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h4 style={{ color: '#fff', fontWeight: 700, marginBottom: 2 }}>Slots / Plans</h4>
          <div style={{ color: '#555', fontSize: 13 }}>{plans.length} plans configured</div>
        </div>
        <button onClick={openAdd} style={{ background: cyan, border: 'none', color: '#000', borderRadius: 8, padding: '9px 20px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
          + Add Plan
        </button>
      </div>

      <div style={{ background: '#111', border: '1px solid rgba(0,229,255,0.1)', borderRadius: 12, overflow: isMobile ? 'auto' : 'hidden', overflowY: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '500px' : '100%' }}>
          <thead>
            <tr style={{ background: 'rgba(0,229,255,0.07)' }}>
              {['#', 'Plan Name', 'Amount (USDT)', 'Type', 'Purchased By', 'Actions'].map((h) => (
                <th key={h} style={{ color: cyan, fontSize: 12, fontWeight: 600, padding: '12px 16px', textAlign: 'left' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>Loading...</td></tr>
            ) : plans.map((p, i) => (
              <tr key={p._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <td style={{ color: '#555', fontSize: 12, padding: '12px 16px' }}>{i + 1}</td>
                <td style={{ color: '#fff', fontSize: 13, fontWeight: 600, padding: '12px 16px' }}>{p.planName}</td>
                <td style={{ color: '#66ff99', fontSize: 14, fontWeight: 700, padding: '12px 16px' }}>{p.amount}</td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{ background: 'rgba(0,229,255,0.1)', color: cyan, borderRadius: 6, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>{p.planType}</span>
                </td>
                <td style={{ color: '#888', fontSize: 13, padding: '12px 16px' }}>{p.purchaseCount} users</td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => openEdit(p)} style={{ background: 'rgba(0,229,255,0.1)', border: '1px solid rgba(0,229,255,0.3)', color: cyan, borderRadius: 6, padding: '4px 12px', fontSize: 12, cursor: 'pointer' }}>Edit</button>
                    <button onClick={() => handleDelete(p._id)} disabled={deletingId === p._id || p.purchaseCount > 0}
                      style={{ background: p.purchaseCount > 0 ? 'transparent' : 'rgba(200,50,50,0.12)', border: `1px solid ${p.purchaseCount > 0 ? 'rgba(255,255,255,0.06)' : 'rgba(200,50,50,0.3)'}`, color: p.purchaseCount > 0 ? '#333' : '#ff6666', borderRadius: 6, padding: '4px 12px', fontSize: 12, cursor: p.purchaseCount > 0 ? 'not-allowed' : 'pointer' }}>
                      {deletingId === p._id ? '...' : 'Delete'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div style={{ background: '#111', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 14, padding: '28px', width: '100%', maxWidth: 380 }}>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 16, marginBottom: 20 }}>{modal === 'add' ? 'Add Plan' : 'Edit Plan'}</div>
            <form onSubmit={handleSave}>
              {[
                { label: 'Plan Name', key: 'planName', type: 'text', placeholder: 'e.g. Slot-01' },
                { label: 'Amount (USDT)', key: 'amount', type: 'number', placeholder: '10' },
              ].map(({ label, key, type, placeholder }) => (
                <div key={key} style={{ marginBottom: 14 }}>
                  <label style={{ color: '#888', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>{label}</label>
                  <input type={type} placeholder={placeholder} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    style={{ width: '100%', background: 'rgba(0,229,255,0.04)', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 14, outline: 'none' }}
                    required />
                </div>
              ))}
              <div style={{ marginBottom: 20 }}>
                <label style={{ color: '#888', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Plan Type</label>
                <select value={form.planType} onChange={(e) => setForm({ ...form, planType: e.target.value })}
                  style={{ width: '100%', background: '#1a1a1a', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 13, outline: 'none' }}>
                  <option value="basic">Basic</option>
                  <option value="premium">Premium</option>
                  <option value="advantage">Advantage</option>
                </select>
              </div>
              {msg && <div style={{ color: '#ff6666', fontSize: 13, marginBottom: 14 }}>{msg}</div>}
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" onClick={() => setModal(null)} style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: '#888', borderRadius: 8, cursor: 'pointer' }}>Cancel</button>
                <button type="submit" disabled={saving} style={{ flex: 1, padding: '10px', background: saving ? 'rgba(255,255,255,0.06)' : cyan, border: 'none', color: saving ? '#555' : '#000', borderRadius: 8, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPlans;
